import logo from "@frontend/assets/logo-white.svg"
import { useAuthRuntime } from "@frontend/components/auth/auth-runtime"
import { Avatar, AvatarFallback } from "@frontend/components/ui/avatar"
import { Button } from "@frontend/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@frontend/components/ui/dropdown-menu"
import { useApiQuery } from "@frontend/hooks/use-api"
import { LogOut, Settings, User } from "lucide-react"
import { useNavigate } from "react-router"

export interface TopbarViewProps {
  userName: string
  userEmail: string
  initials: string
  onLogoClick: () => void
  onSettingsClick: () => void
  onProfileClick: () => void
  onSignOutClick: () => void
}

export function TopbarView({
  userName,
  userEmail,
  initials,
  onLogoClick,
  onSettingsClick,
  onProfileClick,
  onSignOutClick
}: TopbarViewProps) {
  return (
    <header className="flex items-center h-12 px-4 gap-3 bg-card border-b border-border shrink-0">
      <button className="flex items-center gap-2 cursor-pointer shrink-0" onClick={onLogoClick}>
        <div className="size-7 rounded-md flex items-center justify-center">
          <img src={logo} alt="VxAnnotate" className="size-6" />
        </div>
        <span className="text-foreground hidden lg:inline font-medium">VxAnnotate</span>
      </button>

      <div className="flex-1" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full">
            <Avatar className="size-7">
              <AvatarFallback className="text-[11px] bg-primary text-primary-foreground">{initials}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <div className="px-2 py-1.5">
            <p className="text-[12px] font-medium">{userName}</p>
            <p className="text-[11px] text-muted-foreground">{userEmail}</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onSettingsClick}>
            <Settings className="size-3.5 mr-2" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onProfileClick}>
            <User className="size-3.5 mr-2" />
            Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onSignOutClick}>
            <LogOut className="size-3.5 mr-2" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}

export function TopbarContainer() {
  const navigate = useNavigate()
  const { logout } = useAuthRuntime()

  const { data: user } = useApiQuery<{
    id: string
    email: string
    name: string
  }>(["auth", "me"], "/auth/me")

  const initials =
    user?.name
      ?.split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() ?? "VX"

  return (
    <TopbarView
      userName={user?.name ?? "Unknown User"}
      userEmail={user?.email ?? "-"}
      initials={initials}
      onLogoClick={() => navigate("/")}
      onSettingsClick={() => navigate("/settings")}
      onProfileClick={() => navigate("/settings")}
      onSignOutClick={logout}
    />
  )
}

export const Topbar = TopbarContainer
